// PROTOTYPE — custom React Flow node components for the automation canvas.
// Trigger node (the start point) and Action node (a brick). Styling follows the
// existing admin tokens (colors/borders) so the prototype feels native.

import {Handle, Position} from "@xyflow/react";
import {Box, Typography} from "@mui/material";
import {
    BoltOutlined as BoltIcon,
    PersonAddAlt1Outlined as CreateIcon,
    EditOutlined as UpdateIcon,
    PersonRemoveOutlined as DeleteTraineeIcon,
    EmailOutlined as EmailIcon,
    SmsOutlined as SmsIcon,
    HttpOutlined as HttpIcon,
    NotificationsActiveOutlined as PushIcon,
    PlayArrowRounded as PlayIcon,
    PersonOutline as UserIcon,
    FitnessCenterOutlined as WorkoutIcon,
    MonitorWeightOutlined as WeightIcon,
    AssignmentOutlined as FormIcon,
    RestaurantMenuOutlined as NutritionIcon,
    TuneOutlined as DataIcon,
    FilterAltOutlined as FilterIcon,
} from "@mui/icons-material";
import {borders, colors} from "../../ui/colors";
import arboxLogo from "../../../assets/images/arbox.webp";
import fireberryLogo from "../../../assets/images/fireberry.webp";

// System → logo image for the trigger tile (systems without a logo use a glyph).
const SYSTEM_LOGO = {ARBOX: arboxLogo, FIREBERRY: fireberryLogo};

const ACTION_ICON = {
    CREATE_TRAINEE: CreateIcon,
    UPDATE_TRAINEE: UpdateIcon,
    DELETE_TRAINEE: DeleteTraineeIcon,
    ASSIGN_PROGRAM_TEMPLATE: WorkoutIcon,   // FitnessCenter — the workout-program icon
    ASSIGN_NUTRITION_MENU: NutritionIcon,   // RestaurantMenu — the nutrition-menu icon
    SEND_FORM: FormIcon,                    // Assignment — the form icon
    EMAIL: EmailIcon,
    SMS: SmsIcon,
    HTTP: HttpIcon,
    PUSH: PushIcon,
};

// Per-event-type icons for the internal-event triggers.
const EVENT_ICON = {
    USER_CREATED: UserIcon, USER_UPDATED: UserIcon, USER_DELETED: UserIcon,
    WORKOUT_COMPLETED: WorkoutIcon, WEIGHT_UPDATED: WeightIcon, FORM_COMPLETED: FormIcon,
};


// Brand teal — matches the "לקוח חדש" CTA button in TopNav.
const BRAND = "#01BBA7";
const BRAND_DARK = "#019385";
const BRAND_TINT = "rgba(1,187,167,0.10)";
const BRAND_RING = "rgba(1,187,167,0.30)";

// App-icon geometry: a rounded square (iPhone-icon size) with the label
// underneath, outside the tile — so the tile itself stays icon-sized.
const TILE = 84;
const TILE_RADIUS = 20; // ≈ iOS "squircle" corner on an 84px icon
const ICON_SIZE = 36;   // glyph inside a tile
const HANDLE = {width: 8, height: 8, background: BRAND, border: "none"};

const tileBase = {
    width: TILE, height: TILE, borderRadius: `${TILE_RADIUS}px`,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", position: "relative",
    transition: "transform 0.12s, box-shadow 0.15s",
    "&:hover": {transform: "translateY(-1px)"},
};

// Wrapper stacks the tile + caption; RTL, fixed width so captions center + wrap.
const NodeShell = ({children, caption, captionColor}) => (
    <Box sx={{width: 108, display: "flex", flexDirection: "column", alignItems: "center", gap: "7px", direction: "rtl"}}>
        {children}
        <Typography sx={{
            fontSize: "15px", fontWeight: 600, lineHeight: 1.2,
            color: captionColor || colors.textSecondary, textAlign: "center",
            maxWidth: 108, wordBreak: "break-word",
        }}>
            {caption}
        </Typography>
    </Box>
);

const selectedRing = (on) => on ? {boxShadow: `0 0 0 3px ${BRAND_RING}`} : {};

// Small "this is a trigger" bolt badge, pinned to the tile's top-RIGHT corner
// (RTL leading corner) and spilling slightly outside it — like an app-icon badge.
const TriggerBadge = () => (
    <Box sx={{
        position: "absolute", top: -10, right: -10, zIndex: 2,
        width: 30, height: 30, borderRadius: "50%",
        bgcolor: BRAND, border: `2.5px solid ${colors.white}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
    }}>
        <BoltIcon sx={{fontSize: 17, color: colors.white}}/>
    </Box>
);

// Flow reads left → right: Start (leftmost) → Trigger → Actions.
// Source handle = RIGHT edge, target handle = LEFT edge, so edges point right.

// Static origin — filled brand-teal app icon.
export const StartNode = () => (
    <NodeShell caption="התחלה" captionColor={colors.textPrimary}>
        <Box sx={{...tileBase, background: BRAND, color: colors.white, boxShadow: "0 2px 8px rgba(1,187,167,0.30)"}}>
            <PlayIcon sx={{fontSize: ICON_SIZE + 4}}/>
            <Handle type="source" position={Position.Right} style={{background: colors.white, border: `2px solid ${BRAND}`, width: 8, height: 8}}/>
        </Box>
    </NodeShell>
);

export const TriggerNode = ({data, selected}) => {
    const trigger = data?.trigger;
    const configured = Boolean(trigger);
    const logo = trigger && SYSTEM_LOGO[trigger.system];
    const EventIcon = (trigger && EVENT_ICON[trigger.eventType]) || BoltIcon;
    return (
        <NodeShell caption={configured ? trigger.label : "בחר טריגר…"} captionColor={configured ? colors.textPrimary : colors.textMuted}>
            {logo ? (
                // System app icon fills the whole tile; the trigger bolt badge sits
                // on its top-right corner.
                <Box sx={{
                    ...tileBase, p: 0,
                    border: `1.5px solid ${selected ? BRAND : colors.border}`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.10)", ...selectedRing(selected),
                }}>
                    <Handle type="target" position={Position.Left} style={HANDLE}/>
                    <Box component="img" src={logo} alt={trigger.system}
                         sx={{width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit"}}/>
                    <TriggerBadge/>
                    <Handle type="source" position={Position.Right} style={HANDLE}/>
                </Box>
            ) : (
                <Box sx={{
                    ...tileBase, bgcolor: BRAND_TINT,
                    border: `1.5px solid ${selected ? BRAND : BRAND_RING}`,
                    ...selectedRing(selected),
                }}>
                    <Handle type="target" position={Position.Left} style={HANDLE}/>
                    <EventIcon sx={{fontSize: ICON_SIZE, color: BRAND}}/>
                    <Handle type="source" position={Position.Right} style={HANDLE}/>
                </Box>
            )}
        </NodeShell>
    );
};

export const ActionNode = ({data, selected}) => {
    const configured = Boolean(data?.action);
    const Icon = configured ? (ACTION_ICON[data.action.kind] ?? BoltIcon) : BoltIcon;
    const mappedCount = data?.mapping ? Object.values(data.mapping).filter(Boolean).length : 0;
    const needsMap = configured && mappedCount === 0;
    return (
        <NodeShell caption={configured ? data.action.label : "בחר פעולה…"} captionColor={configured ? colors.textPrimary : colors.textMuted}>
            <Box sx={{
                ...tileBase, bgcolor: colors.white,
                border: `1.5px solid ${selected ? BRAND : colors.border}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)", ...selectedRing(selected),
            }}>
                <Handle type="target" position={Position.Left} style={HANDLE}/>
                <Icon sx={{fontSize: ICON_SIZE, color: configured ? BRAND : colors.textMuted}}/>
                {needsMap && (
                    <Box sx={{
                        position: "absolute", top: -3, left: -3,
                        width: 12, height: 12, borderRadius: "50%",
                        bgcolor: colors.warning, border: `2px solid ${colors.white}`,
                    }}/>
                )}
                <Handle type="source" position={Position.Right} style={HANDLE}/>
            </Box>
        </NodeShell>
    );
};

// Data-transform node — a dashed-border tile to read as a "processing" step.
// One brick kind (DATA_TRANSFORM); the operation is chosen inside.
export const DataNode = ({data, selected}) => {
    const brick = data?.brick;
    const Icon = brick?.kind === "FILTER" ? FilterIcon : DataIcon;
    return (
        <NodeShell caption={brick ? brick.label : "עיבוד"} captionColor={brick ? colors.textPrimary : colors.textMuted}>
            <Box sx={{
                ...tileBase, bgcolor: colors.bg,
                border: `1.5px dashed ${selected ? BRAND : colors.textMuted}`,
                ...selectedRing(selected),
            }}>
                <Handle type="target" position={Position.Left} style={HANDLE}/>
                <Icon sx={{fontSize: ICON_SIZE, color: BRAND}}/>
                <Handle type="source" position={Position.Right} style={HANDLE}/>
            </Box>
        </NodeShell>
    );
};

export {BRAND, BRAND_DARK};

export const nodeTypes = {start: StartNode, trigger: TriggerNode, action: ActionNode, data: DataNode};
